/* ----------------------------------------------------------------------------
 * suggestWriteModel — the PURE, DOM-free decision core for Aura P2: "which 3
 * articles should we write for my top keywords?" -> 3 cards on screen + a one-line
 * spoken summary ("I suggest 3 articles for <site>: … Shall I write them?") -> on
 * "yes"/"write them" the 3 drafts are written in BookedCentral, then handed to the
 * P1 Draft/Publish flow (its own safety rules apply: a bare "yes" never publishes).
 * Owner GO 10-10-2026 ~14:54 UTC; owner decision file
 * docs/brain/governance/owner-decision-aura-publish-choice-2026-10-10.md (cards on
 * screen, never read the blog out; confirm by voice or tap).
 *
 * Rules (exercised under node --test):
 *   - the ask detector catches the owner's phrasings and does NOT hijack ordinary
 *     questions ("how do I write a blog?") or the P1 push ask;
 *   - NO wins over YES ("yes… actually no" cancels); a hedge re-asks once, then
 *     cancels; writing never happens on an unclear answer;
 *   - writing only creates BookedCentral drafts; publishing stays a separate,
 *     explicit P1 choice.
 * -------------------------------------------------------------------------- */

import { isTurnClose } from '../wake/wakeMachine.ts';
import { isPureAffirmative, stripQueueRef } from './batchPublishModel.ts';
import type { AiLimit } from './aiWritingLimit.ts';

export const WRITE_MAX_REASKS = 1;
export const WRITE_REASK = 'Sorry, I didn’t catch that. Shall I write them? Say yes or no.';
export const WRITE_CANCEL_ACK = 'Okay, I won’t write them.';
export const WRITE_PICK_SITE = 'Which site? I’ve put the options on screen.';
export const WRITE_NO_SITE = 'I couldn’t find a connected site to suggest articles for.';
/* Blog writing by voice (owner GO 12-10-2026 15:53 UTC, "go blog writing"). One line
 * spoken; the details are on screen (owner brevity rule 10-10-2026). */
export const WRITE_OWNER_OPERATOR_ONLY = 'Only workspace owners and operators can write blogs with Aura.';
export const WRITING_FORBIDDEN_CODE = 'aura_writing_owner_operator_only';
export const SUGGESTED_NONE = 'I haven’t suggested a blog yet. Ask me which blogs to write.';
export const PICK_WHICH = 'Which one — the first, second or third? You can tap it.';
export const PICK_ALREADY = 'That one is already written.';
export const SINGLE_REASK = 'Sorry, I didn’t catch that. Shall I write it? Say yes or no.';
export const SINGLE_CANCEL_ACK = 'Okay, I won’t write it.';
/** Owner 12-10-2026 16:48 UTC (verbatim): "Aura should ask which ones or shall i write
 *  all - if i dont specify". Asked when several cards are open and the answer names none. */
export const WRITE_WHICH = 'Which ones, or shall I write all?';

/** A selection of the open cards: "all" / "all of them" / "both", or ordinals and
 *  numbers ("the first", "1 and 3", "first and third", "the last"). null = none named.
 *  Returns positions into the open list (0-based), unique and in order. */
export function parseSelection(transcript: string, count: number): 'all' | number[] | null {
  const p = norm(transcript);
  if (p === '' || count <= 0) return null;
  if (/ (all|everything|every one|all of them|all three|all 3|each one) /.test(p) || (count === 2 && / both /.test(p))) return 'all';
  const words: Array<[RegExp, RegExp, number]> = [
    [/ (first|1st|1) /, / number one /, 0], [/ (second|2nd|2) /, / number two /, 1], [/ (third|3rd|3) /, / number three /, 2],
  ];
  const picked = new Set<number>();
  // single tokens are matched on a double-spaced copy so adjacent tokens ("1 and 3") both match
  const padded = p.replace(/ /g, '  ');
  for (const [tok, phrase, i] of words) { if ((tok.test(padded) || phrase.test(p)) && i < count) picked.add(i); }
  if (/ last /.test(p)) picked.add(count - 1);
  return picked.size > 0 ? Array.from(picked).sort((a, b) => a - b) : null;
}

export interface SuggestWriteAsk {
  /** what the user called the site, or null. */
  siteHint: string | null;
  /** "write a blog for <keyword>" / "write a blog about <topic>": ONE named blog. */
  single?: { keyword: string };
  /** conversational-actions (owner GO 12-10-2026 20:30 UTC): "write the blog … that is on
   *  the queue" -- names a READY draft (already written). titleHint null = "the one on the
   *  queue". Routed to the P1 publish offer, never to a paid write. */
  queued?: { titleHint: string | null };
  /** "write the blog you suggested" / "write the second one": a card already on screen.
   *  index = 0-based, -1 = last, null = not said (resolved against the cards). */
  pick?: { index: number | null; about: string | null };
}

/** Spoken read-back for one blog: names the action, keyword and site (pure-yes rule). */
export function singleReadbackLine(keyword: string, siteName: string): string {
  return 'Write a blog for “' + keyword + '” on ' + siteName + '?';
}

function norm(transcript: string): string {
  const s = (transcript ?? '')
    .replace(/['’‘`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s === '' ? '' : ' ' + s + ' ';
}

/** Keeps '.' and '/' inside a token so a site name survives (same as P1 normAsk). */
function normAsk(transcript: string): string {
  const s = (transcript ?? '')
    .replace(/['’‘`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9./]+/g, ' ')
    .replace(/[./]+(?=\s|$)/g, ' ')
    .replace(/(^|\s)[./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s === '' ? '' : ' ' + s + ' ';
}

function containsAny(padded: string, tokens: readonly string[]): boolean {
  if (padded === '') return false;
  for (const tok of tokens) if (padded.includes(' ' + tok + ' ')) return true;
  return false;
}

const NOUN = '(?:articles?|blogs?|blog posts?|posts?|pieces?)';
const SITE_STOP = ['my', 'the', 'our', 'top', 'it', 'me', 'us', 'wordpress', 'website', 'site', 'blog', 'keywords', 'keyword', 'seo', 'most'];

const SINGLE_NOUN = '(?:blog posts?|blogs?|articles?|posts?|pieces?)';
const SITE_TAIL = / (?:on|for|to|at) (?:the )?(?:site |website )?((?:[a-z0-9-]+\.)+[a-z]{2,6}(?:\/[a-z0-9-]+)?|[a-z0-9-]+ dot [a-z]{2,6}(?: dot [a-z]{2})?(?: slash [a-z0-9-]+)?|partners|the partners site)$/;
const KEYWORD_FILLER_TAIL = /(?: please| for me| for us| now| today| thanks| thank you)+$/;
const PICK_REF = /(?: you (?:suggested|recommended|proposed)| (?:that|this) (?:one|blog|article|post|blog post)| the (?:suggested|recommended) (?:one|blog|article|post)| the one about| the one on| the one for)/;
const PICK_THING = '(?:one|blog post|blog|article|post|piece|keyword|suggestion)';
/** An ordinal counts only as "the <ordinal> <thing>" or "number <n>" — never a bare
 *  "first"/"top" ("which blogs should we write first", "my top keywords"). */
const ORDINALS: Array<[RegExp, number]> = [
  [new RegExp(` the (?:first|1st) ${PICK_THING} | number (?:one|1) `), 0],
  [new RegExp(` the (?:second|2nd|middle) ${PICK_THING} | number (?:two|2) `), 1],
  [new RegExp(` the (?:third|3rd) ${PICK_THING} | number (?:three|3) `), 2],
  [new RegExp(` the last ${PICK_THING} `), -1],
];

/** "write the blog you suggested" / "write that one" / "write the second one" /
 *  "write the one about sound baths". Needs a write verb AND a reference to a
 *  suggestion or an ordinal + one/blog/article. Never a push/publish. */
export function detectPickAsk(transcript: string): { index: number | null; about: string | null } | null {
  const p = normAsk(transcript);
  if (p === '') return null;
  if (/ (push|publish|upload|send|release) /.test(p)) return null;
  if (!/ (write|do|create|draft|go with) /.test(p)) return null;
  let index: number | null = null;
  for (const [re, i] of ORDINALS) {
    if (re.test(p)) { index = i; break; }
  }
  const ref = PICK_REF.test(p);
  if (index === null && !ref) return null;
  const aboutM = / the one (?:about|on|for) (.+?) $/.exec(p);
  return { index, about: aboutM ? aboutM[1].trim() : null };
}

/** "write a blog for <keyword>" / "write an article about <topic>" (+ optional site). */
export function detectSingleBlogAsk(transcript: string): { keyword: string; siteHint: string | null; queued?: boolean } | null {
  const p = normAsk(transcript);
  if (p === '') return null;
  if (/ (push|publish|upload|send|release) /.test(p)) return null;               // P1 territory
  if (/ how (do|can|should) (i|we|you) /.test(p)) return null;                   // how-to question
  // conversational-actions (diagnosis F5, owner session 20:11:58 / 20:14:56): also "the blog
  // regarding …", "the most important blog that is recommended for <keyword>". The read-back
  // + server cap check still guard every write, so wider matching never spends without a yes.
  const m = new RegExp(` (?:write|create|draft) (?:me |us )?(?:the |a |an |one |1 |another |a new |new |that |this )?(?:(?:most important|most recommended|top|best|next|main|recommended|suggested|new) )*${SINGLE_NOUN} (?:(?:that is|thats|that s|which is|that was|which was) )?(?:(?:recommended|suggested|proposed) )?(?:for|about|on|around|covering|targeting|regarding|called|titled|named) (.+?) $`).exec(p);
  if (!m) return null;
  let rest = ' ' + m[1].trim();
  const q = stripQueueRef(rest + ' ');
  const queued = q.queue;
  if (queued) rest = ' ' + q.rest.trim();
  let siteHint: string | null = null;
  const st = SITE_TAIL.exec(rest);
  if (st) {
    siteHint = st[1].replace(/ dot /g, '.').replace(/ slash /g, '/').replace(/^the partners site$/, 'partners');
    rest = rest.slice(0, st.index);
  }
  rest = rest.replace(KEYWORD_FILLER_TAIL, '').trim();
  rest = rest.replace(/^(?:the )?(?:keyword|topic|phrase|term) /, '').replace(/^(?:the )?(?:keyword|topic) of /, '').trim();
  // generic "my top keywords" -> the 3-card suggestion (P2), not one named blog
  if (/^(?:my |our |the )?(?:top |most important |main |best )*(?:\d+ |three |two |five )?keywords?$/.test(rest)) return null;
  // "write a blog for that one / it" -> a pick of the suggestion, not a keyword
  if (/^(?:it|that|this|that one|this one|them|those)$/.test(rest)) return null;
  if (queued) return { keyword: rest, siteHint, queued: true };
  if (rest.length < 2 || rest.length > 120) return null;
  return { keyword: rest, siteHint };
}

/**
 * Detect the P2 ask. Deterministic, closed set, OFF the model. Matches e.g.
 *   "which 3 blog articles should we write?", "what articles should I write next",
 *   "suggest 3 articles for my top keywords", "write 3 articles for my top keywords",
 *   "which blogs should we write to rank our most important keywords".
 * Never matches a push/publish ask (that is P1) or a how-to question.
 */
export function detectSuggestWriteAsk(transcript: string): SuggestWriteAsk | null {
  // Blog writing by voice (12-10-2026): the single-blog and pick asks are checked
  // FIRST, so "write a blog for reiki london" never becomes a 3-card suggestion.
  const single = detectSingleBlogAsk(transcript);
  if (single?.queued) return { siteHint: single.siteHint, queued: { titleHint: single.keyword !== '' ? single.keyword : null } };
  if (single) return { siteHint: single.siteHint, single: { keyword: single.keyword } };
  // "write the one on the queue" / "do the queued one": a READY draft -> the publish offer.
  {
    const pq = normAsk(transcript);
    if (/ (write|do|create|draft) /.test(pq) && !/ (push|publish|upload|send|release) /.test(pq)) {
      const q = stripQueueRef(pq);
      if (q.queue && /^ (?:(?:hey )?aura |ora )?(?:(?:can|could|would|will) you )?(?:please )?(?:write|do|create|draft) (?:the |that |this |my )?(?:one|blog|draft|post|article|blog post)? ?(?:please )?$/.test(q.rest)) {
        return { siteHint: null, queued: { titleHint: null } };
      }
    }
  }
  const pick = detectPickAsk(transcript);
  if (pick) return { siteHint: null, pick };
  const p = normAsk(transcript);
  if (p === '') return null;
  if (/ (push|publish|upload|send|release) /.test(p)) return null;               // P1 territory
  if (/ how (do|can|should) (i|we|you) /.test(p)) return null;                   // how-to question
  const whichShouldWrite = new RegExp(` (which|what) (?:\\w+ ){0,4}${NOUN} (?:\\w+ ){0,4}(write|create) `).test(p);
  // conversational-actions: "recommended"/"suggested" too (diagnosis F5: "recommend " missed "recommended").
  const suggest = new RegExp(` (suggest|recommend|propose)(?:s|ed)? (?:\\w+ ){0,4}${NOUN} `).test(p);
  const writeForKeywords = new RegExp(` (write|create) (?:\\w+ ){0,3}${NOUN} (?:\\w+ ){0,6}keywords? `).test(p);
  if (!whichShouldWrite && !suggest && !writeForKeywords) return null;
  // Site hint: a dotted name after to/on/for ("for booksesh.com"), or a bare name
  // after on/for ("for partners"). "to <verb>" ("…to rank…") is never a site.
  let siteHint: string | null = null;
  const re = / (to|on|for) (?:the )?(?:site )?([a-z0-9.\-/]+(?: dot [a-z]{2,6}(?: dot [a-z]{2})?)?)(?= )/g;
  for (let m = re.exec(p); m; m = re.exec(p)) {
    const s = m[2].replace(/ dot /g, '.').replace(/^www\./, '').replace(/\/$/, '');
    if (SITE_STOP.includes(s) || !/[a-z]/.test(s)) continue;
    if (s.includes('.') || (m[1] !== 'to' && s.length > 3)) { siteHint = s; break; }
  }
  return { siteHint };
}

export type WriteAnswer = { kind: 'write' } | { kind: 'no' } | { kind: 'unrecognised' };

const NO_TOKENS = ['no', 'nope', 'nah', 'not', 'never', 'cancel', 'dont', 'do not', 'not now', 'not yet', 'leave it', 'hold on', 'hold off', 'wait', 'later', 'stop', 'skip'];
/** Explicit write verbs confirm on their own ("write them", "write it"). */
const WRITE_VERB_TOKENS = ['write them', 'write it', 'write them all', 'write all', 'write all three', 'write all 3', 'write both', 'write', 'go ahead and write'];
/** Plain affirmatives confirm ONLY when PURE (C-AJ1 / owner wording rule 10-10-2026:
 *  "A PURE 'yes' confirms ONLY a read-back that already names the action, site and
 *  count. A qualified 'yes' gets a re-ask"). The read-back names all three. */
const YES_TOKENS = ['yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'go ahead', 'do it', 'please do', 'go for it', 'sounds good', 'yes please', 'confirm'];
const HEDGE_TOKENS = ['maybe', 'perhaps', 'i guess', 'unsure', 'hmm', 'um', 'dunno', 'dont know', 'what', 'which', 'why', 'how'];

/** Match a spoken/typed answer to "Shall I write them?". NO > hedge > WRITE. */
export function matchWriteAnswer(transcript: string): WriteAnswer {
  const p = norm(transcript);
  if (p === '') return { kind: 'unrecognised' };
  if (isTurnClose(transcript) || containsAny(p, NO_TOKENS)) return { kind: 'no' };
  if (containsAny(p, HEDGE_TOKENS)) return { kind: 'unrecognised' };
  if (containsAny(p, WRITE_VERB_TOKENS)) return { kind: 'write' };
  if (containsAny(p, YES_TOKENS)) return isPureAffirmative(p) ? { kind: 'write' } : { kind: 'unrecognised' };
  return { kind: 'unrecognised' };
}

export type WriteStep = { kind: 'write' } | { kind: 're-ask' } | { kind: 'cancel' };

export function resolveWriteAnswer(match: WriteAnswer, reAskCount: number): WriteStep {
  if (match.kind === 'write') return { kind: 'write' };
  if (match.kind === 'no') return { kind: 'cancel' };
  return reAskCount >= WRITE_MAX_REASKS ? { kind: 'cancel' } : { kind: 're-ask' };
}

/** After the run: one short line (the P1 cards then ask draft-or-publish). */
export function writeDoneLine(written: number, other: number): string {
  if (written === 0) return other > 0 ? 'I couldn’t write any of them — the reasons are on the cards.' : 'Nothing was written.';
  const lead = written === 1 ? 'I’ve written 1 draft' : `I’ve written ${written} drafts`;
  return other > 0 ? `${lead}; ${other} couldn’t be written — see the cards.` : `${lead}.`;
}

/** Which on-screen card a pick names: an index, 'ambiguous', or 'none' (no cards). */
export function resolvePick(pick: { index: number | null; about: string | null }, items: readonly { index: number; keyword: string }[]): number | 'ambiguous' | 'none' {
  if (items.length === 0) return 'none';
  if (pick.index !== null) {
    const i = pick.index < 0 ? items.length - 1 : pick.index;
    return i >= 0 && i < items.length ? items[i].index : 'ambiguous';
  }
  if (pick.about) {
    const a = norm(pick.about).trim();
    const hit = items.filter((c) => { const k = norm(c.keyword).trim(); return k !== '' && (k.includes(a) || a.includes(k)); });
    if (hit.length === 1) return hit[0].index;
  }
  return items.length === 1 ? items[0].index : 'ambiguous';
}

export function decisionLabel(decision: string, targetPage: string | null): string {
  return decision === 'refresh' ? 'Refresh: new supporting draft for ' + (targetPage ?? 'the existing page') : 'New article';
}

// ---- shared view model (rendered by SuggestWritePanel in chat AND voice) -----

export interface SuggestViewCard {
  index: number;
  keyword: string;
  position: number | null;
  decision: string;
  target_page: string | null;
  target_page_title: string;
  /** single blog: the page the article links to (ranked page or best-matched page). */
  link_target?: string | null;
  working_title: string;
  outline: string[];
  note: string;
}

export interface SuggestItemResult {
  status: 'writing' | 'written' | 'duplicate' | 'not_synced' | 'failed' | 'refused' | 'skipped' | string;
  line: string;
  draft_id: number | null;
}

export interface SuggestWriteView {
  phase: 'loading' | 'choose_site' | 'asking' | 'limit' | 'writing' | 'done' | 'cancelled' | 'none' | 'error';
  channel: 'chat' | 'voice' | 'tap';
  /** 'suggest' = the 3-card suggestion; 'single' = one named blog. */
  kind: 'suggest' | 'single';
  /** the card the open read-back is about (a pick of a suggestion), or null = all. */
  pick: number | null;
  /** single blog: the keyword waiting for its site choice (choose_site phase). */
  pendingKeyword: string | null;
  /** the monthly AI writing limit, when reached: shown as a clear warning. */
  limit: AiLimit | null;
  sites: { id: number; label: string; url: string; name: string; projectId: number | null }[];
  site: { id: number; name: string; label: string; url: string; projectId: number | null } | null;
  items: SuggestViewCard[];
  line: string;
  writeAvailable: boolean;
  limitMessage: string;
  results: Record<number, SuggestItemResult>;
  resultLine: string;
  error: string;
}

export function emptySuggestView(channel: SuggestWriteView['channel']): SuggestWriteView {
  return { phase: 'loading', channel, kind: 'suggest', pick: null, pendingKeyword: null, limit: null, sites: [], site: null, items: [], line: '', writeAvailable: false, limitMessage: '', results: {}, resultLine: '', error: '' };
}
